import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {BrandKitSwatchCluster} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster';
import {BrandKitSwatchClusterVariant} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster.types';
import {VisuallyHiddenText} from '@Components/visually-hidden-text/visually-hidden-text';
import {LabeledValueRow} from '@AiChat/components/labeled-value-row/labeled-value-row';
import type {SavedAccountBrandKit} from '@AiChat/ai-chat.types';
import {MEMORY_ACCOUNT_KIT_COLOR_LIMIT} from './ai-chat-memory-account-section.types';
import type {AiChatMemoryAccountSectionProps, MemoryAccountSetUpAction} from './ai-chat-memory-account-section.types';
import styles from './ai-chat-memory-account-section.module.scss';

export function AiChatMemoryAccountSection({account}: AiChatMemoryAccountSectionProps): ReactElement {
  const editLabel = window.i18next.t('pmwjs_ai_memory_account_edit');

  const getNameRow = (): ReactElement | null => {
    if (!account.accountName) {
      return null;
    }

    return (
      <LabeledValueRow label={window.i18next.t('pmwjs_ai_memory_account_name')} action={{label: editLabel, href: account.editUrls.accountName}}>
        <Text size={TextSize.MD} val={account.accountName} className={styles.memoryAccountValue} />
      </LabeledValueRow>
    );
  };

  const getKitColorsName = (colorHexes: string[]): ReactElement | null => {
    if (colorHexes.length === 0) {
      return null;
    }

    return <VisuallyHiddenText val={window.i18next.t('pmwjs_ai_memory_account_kit_colors', {colors: colorHexes.join(', ')})} />;
  };

  const getBrandKit = (brandKit: SavedAccountBrandKit, kitIndex: number): ReactElement => {
    const colorHexes = brandKit.colors.map((color) => color.hex);

    return (
      <li key={`${kitIndex}-${brandKit.name}`} className={styles.memoryAccountKit}>
        <Text size={TextSize.MD} val={brandKit.name} className={styles.memoryAccountValue} />
        <BrandKitSwatchCluster colorHexes={colorHexes} variant={BrandKitSwatchClusterVariant.SPACED} colorLimit={MEMORY_ACCOUNT_KIT_COLOR_LIMIT} />
        {getKitColorsName(colorHexes)}
      </li>
    );
  };

  const getBrandKitsRow = (): ReactElement | null => {
    if (account.brandKits.length === 0) {
      return null;
    }

    return (
      <LabeledValueRow label={window.i18next.t('pmwjs_ai_memory_account_brand_kits')} action={{label: editLabel, href: account.editUrls.brandKits}}>
        <ul className={styles.memoryAccountKitList}>{account.brandKits.map(getBrandKit)}</ul>
      </LabeledValueRow>
    );
  };

  const getProfileRow = (): ReactElement | null => {
    const profile = account.businessProfile;
    if (!profile) {
      return null;
    }

    const summaryParts = [profile.companyName, profile.phone, profile.email, profile.website].filter((part) => part !== '');

    return (
      <LabeledValueRow label={window.i18next.t('pmwjs_ai_memory_account_profile')} action={{label: editLabel, href: account.editUrls.businessProfile}}>
        <Text size={TextSize.MD} val={summaryParts.join(' · ')} className={styles.memoryAccountValue} />
      </LabeledValueRow>
    );
  };

  const getSetUpAction = (): MemoryAccountSetUpAction => {
    if (account.canCreateBrandKit) {
      return {text: window.i18next.t('pmwjs_add_a_brand_kit'), href: account.editUrls.brandKits};
    }

    return {text: window.i18next.t('pmwjs_ai_memory_account_add_profile'), href: account.editUrls.businessProfile};
  };

  const getSetUpButton = (): ReactElement | null => {
    if (account.brandKits.length > 0 || account.businessProfile) {
      return null;
    }

    const setUpAction = getSetUpAction();

    return <Button text={setUpAction.text} href={setUpAction.href} type={ButtonType.SECONDARY} size={ButtonSize.MEDIUM} icon="icon-plus" customClasses={styles.memoryAccountSetUpButton} />;
  };

  return (
    <section className={styles.memoryAccountSection}>
      <Text size={TextSize.LG} bold val={window.i18next.t('pmwjs_ai_memory_account_heading')} />
      <ul className={styles.memoryAccountRows}>
        {getNameRow()}
        {getBrandKitsRow()}
        {getProfileRow()}
      </ul>
      {getSetUpButton()}
    </section>
  );
}
